import {
  ReceiptLine,
  ReceiptLineLabel,
  ReceiptLineValue,
} from "@babackapp/customer-ui/components/receipt-line";
import { Separator } from "@babackapp/customer-ui/components/separator";
import type { ReturnFinancials } from "../hooks/use-return-financials";
import { PRICE_PLACEHOLDER } from "../hooks/use-hypothetical-return-fee";
import { formatPrice } from "../utils/format-price";

type Props = {
  financials: ReturnFinancials;
  t: (key: string, params?: Record<string, unknown>) => string;
  bonusCurrency: string;
};

/**
 * Gross totals, then what is added and taken off, then the net the customer receives.
 * Same breakdown as the legacy receipt, so both portals announce the same amount.
 */
export function FinancialRecap({ financials, t, bonusCurrency }: Props) {
  const {
    summary,
    fee,
    feeTax,
    isFeePending,
    bonusTotal,
    initialShippingRefund,
  } = financials;
  const currency = summary.currency;
  const hasNet =
    financials.netRefundTotal > 0 || financials.netDiscountTotal > 0;

  return (
    <>
      {summary.refundTotal > 0 && (
        <Line label={t("financial.refund")}>
          {formatPrice(summary.refundTotal, currency)}
        </Line>
      )}
      {summary.discountTotal > 0 && (
        <Line label={t("financial.discountOrCredit")}>
          {formatPrice(summary.discountTotal, currency)}
        </Line>
      )}
      {summary.exchangeTotal > 0 && (
        <Line label={t("financial.exchange")}>
          {formatPrice(summary.exchangeTotal, currency)}
        </Line>
      )}
      {bonusTotal > 0 && (
        <Line label={t("financial.bonus")} className="text-green-600">
          +{formatPrice(bonusTotal, bonusCurrency)}
        </Line>
      )}
      {initialShippingRefund > 0 && (
        <Line label={t("financial.initialShipping")} className="text-green-600">
          +{formatPrice(initialShippingRefund, currency)}
        </Line>
      )}
      {(isFeePending || fee > 0) && (
        <Line label={t("financial.returnFee")}>
          {isFeePending ? PRICE_PLACEHOLDER : `-${formatPrice(fee, currency)}`}
        </Line>
      )}
      {!isFeePending && feeTax > 0 && (
        <p className="text-xs text-muted-foreground">
          {t("financial.returnFeeTaxIncluded", {
            tax: formatPrice(feeTax, currency),
          })}
        </p>
      )}
      {hasNet && <Separator />}
      {financials.netRefundTotal > 0 && (
        <Line label={t("financial.refundTotal")} emphasis>
          {net(financials.netRefundTotal, currency, isFeePending)}
        </Line>
      )}
      {financials.netDiscountTotal > 0 && (
        <Line label={t("financial.discountTotal")} emphasis>
          {net(financials.netDiscountTotal, currency, isFeePending)}
        </Line>
      )}
      {summary.amountDue > 0 && (
        <>
          <Separator />
          <Line label={t("financial.amountToPay")} emphasis>
            {formatPrice(summary.amountDue, currency)}
          </Line>
        </>
      )}
    </>
  );
}

/** The fee is still in flight: show nothing rather than an amount about to drop. */
function net(amount: number, currency: string, isFeePending: boolean): string {
  return isFeePending ? PRICE_PLACEHOLDER : formatPrice(amount, currency);
}

function Line({
  label,
  className,
  emphasis,
  children,
}: {
  label: string;
  className?: string;
  emphasis?: boolean;
  children: React.ReactNode;
}) {
  const emphasisClass = emphasis ? "font-medium text-foreground" : undefined;
  return (
    <ReceiptLine>
      <ReceiptLineLabel className={emphasisClass}>{label}</ReceiptLineLabel>
      <ReceiptLineValue className={className ?? emphasisClass}>
        {children}
      </ReceiptLineValue>
    </ReceiptLine>
  );
}
